import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import Grid from "../common/Grid";
import { GiAlarmClock } from "react-icons/gi";
import Utils from "../../utils";
import { runbookSchedulerActions } from "../../actions/runbookScheduler.actions";
import moment from "moment";
import cronstrue from "cronstrue";
import { MdError, MdDelete, MdEdit } from "react-icons/md";
import { TiTick } from "react-icons/ti";
import ConfirmModal from "../common/ConfirmModal";
import GridHelper from "../helpers/gridHelper";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../contexts/TourContext";
import { usePermissions } from "../../hooks/usePermissions";
import { Alert, Button, Badge } from "react-bootstrap";

function SchedulerGrid(props) {
  const { hideTitle, onEditScheduler } = props;
  const { hasPermission } = usePermissions(props.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const schedulerReducer = useSelector((state) => state.scheduler);
  const { activeTour, endTour } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);
  const [deleteSchedulerId, setDeleteSchedulerId] = useState("");
  const [selectedScheduler, setSelectedScheduler] = useState(null);

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;

    dispatch(runbookSchedulerActions.get(queryParams));
    // eslint-disable-next-line
  }, [location]);

  const handleDeleteScheduler = () => {
    setConfirmDeleteModalShow(false);
    let requestParameters = {
      schedulerId: deleteSchedulerId,
      clientid: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    dispatch(runbookSchedulerActions.remove(requestParameters));
  };

  useEffect(() => {
    if (activeTour === "schedulers") {
      setRunTour(true);
    }
  }, [activeTour]);

  const tourSteps = [
    {
      target: "body",
      content: "Welcome to My Schedulers! Here you can manage automated runbook executions on a schedule.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="scheduler-grid"]',
      content: "This grid shows all your scheduled runbook executions. Click on any row to select it and enable the action buttons.",
      spotlightClicks: true,
      disableBeacon: true,
    },
    ...(hasPermission("schedules.edit") || hasPermission("schedules.delete") ? [{
      target: '[data-tour="action-buttons"]',
      content: "Once you've selected a scheduler, use these buttons to edit or delete it.",
    }] : []),
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      endTour("schedulers");
    }
  };

  let displayColumns = [
    "schedulerId",
    "runbookId",
    "runbookName",
    "cron",
    "nextExecution",
    "timeZone",
    "cronError",
    "active",
    "createdBy",
  ];
  if (props.user.isSuperUser) displayColumns.push("clientName");

  // Check if user has permission to view schedules
  if (!hasPermission("schedules.view")) {
    return (
      <>
        {!hideTitle && <Title text="My Schedulers" icon={<GiAlarmClock className="mb-1" />} />}
        <Alert variant="danger">
          You do not have permission to view schedules. Please contact your administrator.
        </Alert>
      </>
    );
  }

  const data = schedulerReducer.paginatedResult?.data.map((r) => {
    return {
      ...r,
      nextExecution: r.nextExecution
        ? moment(Utils.convertToDateTime(r.nextExecution)).fromNow()
        : "-",
      cron: (
        <>
          {r.cron}
          <br />
          <small>({cronstrue.toString(r.cron)})</small>
        </>
      ),
      cronError: r.cronError ? (
        <span className="text-danger">
          <MdError className="me-1 mb-1" /> Error
        </span>
      ) : (
        <span className="text-success">
          <TiTick className="me-1 mb-1" /> No Error
        </span>
        ),
        timeZone: r.timeZoneId || "-",
        active: r.active ? (
          <Badge bg="success">Active</Badge>
        ) : (
          <Badge bg="secondary">Inactive</Badge>
        ),
    };
  });

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: "#cf7f00",
            textColor: "#334155",
            backgroundColor: "#ffffff",
            arrowColor: "#ffffff",
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: "#cf7f00",
            borderRadius: "0.5rem",
            fontSize: "14px",
            padding: "10px 20px",
          },
          buttonBack: {
            color: "#cf7f00",
            marginRight: "10px",
          },
          buttonSkip: {
            color: "#64748b",
          },
          tooltip: {
            borderRadius: "0.75rem",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          },
          tooltipContent: {
            padding: "20px",
          },
        }}
      />
      {!hideTitle && <Title text="My Schedulers" icon={<GiAlarmClock className="mb-1" />} />}

      {/* Action Buttons Bar */}
      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded" data-tour="action-buttons"
           style={{
             opacity: selectedScheduler ? 1 : 0.6,
             transition: 'opacity 0.3s ease'
           }}>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedScheduler || !hasPermission("schedules.edit")}
          onClick={() => {
            if (selectedScheduler) {
              if (onEditScheduler) {
                onEditScheduler(selectedScheduler.schedulerId);
              } else {
                navigate(`edit?schedulerId=${selectedScheduler.schedulerId}`);
              }
            }
          }}
        >
          <MdEdit className="mb-1 me-2" />
          Edit
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedScheduler || !hasPermission("schedules.delete")}
          onClick={() => {
            if (selectedScheduler) {
              setDeleteSchedulerId(selectedScheduler.schedulerId);
              setConfirmDeleteModalShow(true);
            }
          }}
        >
          <MdDelete className="mb-1 me-2" />
          Delete
        </Button>

        {selectedScheduler && (
          <div className="ms-auto align-self-center">
            <small className="text-muted">
              Selected: <strong>{selectedScheduler.schedulerId} - {selectedScheduler.runbookName}</strong>
            </small>
          </div>
        )}
      </div>

      <div data-tour="scheduler-grid">
        <Grid
          data={data}
          displayColumns={displayColumns}
          idColumn={"schedulerId"}
          selectedRunbook={selectedScheduler}
          onRowClick={(scheduler) => setSelectedScheduler(scheduler)}
          pageNumber={(schedulerReducer.paginatedResult?.pageNumber ?? 1) - 1}
          totalPages={schedulerReducer.paginatedResult?.totalPages ?? 0}
          loading={schedulerReducer.loading}
          onPageChange={GridHelper.handlePageClick}
          onSort={GridHelper.handleSortClick}
        ></Grid>
      </div>

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Delete scheduler"
        question={`Are you sure you want to delete ${deleteSchedulerId}?`}
        noButtonText="No"
        yesButtonText="Yes, please"
        handleClose={() => {
          setConfirmDeleteModalShow(false);
        }}
        handleConfirm={() => {
          handleDeleteScheduler();
        }}
      />
    </>
  );
}

export default SchedulerGrid;
